import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, Users, Phone, Mail, MapPin, MessageSquare, Shield, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { TOURNAMENT, WHATSAPP_LINK } from "@/data/tournament";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPLETE_WHATSAPP_LINK = `https://wa.me/${TOURNAMENT.whatsappNumber}?text=${encodeURIComponent(
  "Quiero completar la inscripción de mi equipo al torneo de Fútbol 7 de Proxima League"
)}`;

const INITIAL = {
  team_name: "",
  captain_name: "",
  phone: "",
  email: "",
  city: "Girona",
  players_count: "",
  comments: "",
  privacy_accepted: false,
  marketing_accepted: false,
};

const Inscripciones = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/registrations`, {
        ...form,
        players_count: parseInt(form.players_count, 10),
        comments: form.comments?.trim() || null,
      });
      setStatus("success");
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "No se ha podido enviar la solicitud. Inténtalo de nuevo.";
      setError(typeof detail === "string" ? detail : "Error al enviar.");
      setStatus("error");
    }
  };

  return (
    <section
      id="inscripciones"
      data-testid="inscripciones-section"
      className="relative bg-[#070707] py-24 sm:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left column - context */}
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.3em] uppercase text-[#EAB308] mb-4">
              / 05 · Inscripciones
            </div>
            <h2
              className="font-display text-5xl sm:text-6xl uppercase tracking-tight leading-none"
              data-testid="inscripciones-title"
            >
              Solicita la
              <br />
              inscripción de
              <br />
              <span className="text-[#EAB308]">tu equipo.</span>
            </h2>
            <p className="mt-8 text-white/65 font-body text-base leading-relaxed max-w-md">
              Primer Torneo Piloto de Fútbol 7 de PROXIMA LEAGUE. Las
              inscripciones se abren el 1 de agosto. Rellena los datos básicos y
              nuestro equipo se pondrá en contacto contigo en menos de 24 horas
              para cerrar tu plaza.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { icon: <Shield className="w-4 h-4" strokeWidth={1.5} />, k: "Plaza", v: `${TOURNAMENT.price}€ por equipo` },
                { icon: <Users className="w-4 h-4" strokeWidth={1.5} />, k: "Plantilla", v: "8 — 12 jugadores" },
                { icon: <CalendarDays className="w-4 h-4" strokeWidth={1.5} />, k: "Fecha", v: "25 de octubre · una jornada" },
                { icon: <MapPin className="w-4 h-4" strokeWidth={1.5} />, k: "Sede", v: "Totem Sports Center · Tordera" },
              ].map((row, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 border-b border-white/10 pb-4"
                >
                  <div className="text-[#EAB308]">{row.icon}</div>
                  <div className="flex-1 flex items-baseline justify-between gap-4">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
                      {row.k}
                    </span>
                    <span className="text-sm sm:text-base text-white font-body text-right">
                      {row.v}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 p-5 border border-white/10 bg-[#0a0a0a]">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
                ¿Prefieres hablarlo directamente?
              </div>
              <a
                href={COMPLETE_WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="inscripciones-whatsapp-direct"
                className="text-white hover:text-[#EAB308] transition-colors text-sm flex items-center gap-2"
              >
                Escríbenos por WhatsApp <span>→</span>
              </a>
            </div>
          </div>

          {/* Right column - form */}
          <div className="lg:col-span-7">
            <div className="relative border border-white/10 bg-[#0a0a0a] p-6 sm:p-10">
              <div className="absolute top-0 left-0 w-3 h-px bg-[#EAB308]" />
              <div className="absolute top-0 left-0 h-3 w-px bg-[#EAB308]" />
              <div className="absolute bottom-0 right-0 w-3 h-px bg-[#EAB308]" />
              <div className="absolute bottom-0 right-0 h-3 w-px bg-[#EAB308]" />

              {status === "success" ? (
                <SuccessState whatsappLink={COMPLETE_WHATSAPP_LINK} onReset={() => setStatus("idle")} />
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="inscripciones-form">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 bg-[#EAB308]" />
                    <span className="text-[10px] tracking-[0.3em] uppercase text-white/60">
                      Formulario de solicitud
                    </span>
                  </div>

                  <Field
                    label="Nombre del equipo"
                    required
                    icon={<Shield className="w-4 h-4" strokeWidth={1.5} />}
                  >
                    <input
                      type="text"
                      required
                      value={form.team_name}
                      onChange={update("team_name")}
                      placeholder="Ej. ATLÈTIC GIRONÍ"
                      data-testid="form-team-name"
                      className="inputx"
                    />
                  </Field>

                  <Field
                    label="Nombre y apellidos del capitán"
                    required
                    icon={<Users className="w-4 h-4" strokeWidth={1.5} />}
                  >
                    <input
                      type="text"
                      required
                      value={form.captain_name}
                      onChange={update("captain_name")}
                      placeholder="Nombre completo"
                      data-testid="form-captain-name"
                      className="inputx"
                    />
                  </Field>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Teléfono"
                      required
                      icon={<Phone className="w-4 h-4" strokeWidth={1.5} />}
                    >
                      <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+34 600 000 000"
                        data-testid="form-phone"
                        className="inputx"
                      />
                    </Field>

                    <Field
                      label="Email"
                      required
                      icon={<Mail className="w-4 h-4" strokeWidth={1.5} />}
                    >
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        placeholder="email@ejemplo.com"
                        data-testid="form-email"
                        className="inputx"
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Ciudad"
                      required
                      icon={<MapPin className="w-4 h-4" strokeWidth={1.5} />}
                    >
                      <input
                        type="text"
                        required
                        value={form.city}
                        onChange={update("city")}
                        placeholder="Girona"
                        data-testid="form-city"
                        className="inputx"
                      />
                    </Field>

                    <Field
                      label="Nº jugadores aprox."
                      required
                      icon={<Users className="w-4 h-4" strokeWidth={1.5} />}
                    >
                      <input
                        type="number"
                        required
                        min={8}
                        max={12}
                        value={form.players_count}
                        onChange={update("players_count")}
                        placeholder="8 — 12"
                        data-testid="form-players-count"
                        className="inputx"
                      />
                    </Field>
                  </div>

                  <Field
                    label="Breve descripción del equipo (opcional)"
                    icon={<MessageSquare className="w-4 h-4" strokeWidth={1.5} />}
                  >
                    <textarea
                      rows={3}
                      value={form.comments}
                      onChange={update("comments")}
                      placeholder="¿Qué os hace diferentes? ¿Por qué vais a ganar el torneo?"
                      data-testid="form-comments"
                      className="inputx resize-none"
                    />
                  </Field>

                  {status === "error" && (
                    <div
                      data-testid="form-error"
                      className="flex items-start gap-3 border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300"
                    >
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <span>{error}</span>
                    </div>
                  )}

                  <label className="flex items-start gap-3 cursor-pointer select-none pt-1">
                    <input
                      type="checkbox"
                      required
                      checked={form.privacy_accepted}
                      onChange={(e) =>
                        setForm({ ...form, privacy_accepted: e.target.checked })
                      }
                      data-testid="form-privacy-checkbox"
                      className="mt-0.5 w-4 h-4 accent-[#EAB308] flex-shrink-0"
                    />
                    <span className="text-[11px] leading-relaxed text-white/50">
                      Consiento el tratamiento de mis datos con la finalidad de
                      gestionar la inscripción de mi equipo en el torneo,
                      conforme a la{" "}
                      <Link
                        to="/politica-privacidad"
                        className="underline hover:text-[#EAB308] transition-colors"
                        data-testid="form-privacy-link"
                      >
                        política de protección de datos
                      </Link>
                      , y acepto la{" "}
                      <Link
                        to="/politica-devoluciones"
                        className="underline hover:text-[#EAB308] transition-colors"
                        data-testid="form-legal-link"
                      >
                        política de devoluciones y cancelaciones
                      </Link>
                      , incluida la publicación del nombre del equipo, capitán y
                      ciudad en la web una vez confirmada la inscripción.{" "}
                      <span className="text-[#EAB308]">*</span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.marketing_accepted}
                      onChange={(e) =>
                        setForm({ ...form, marketing_accepted: e.target.checked })
                      }
                      data-testid="form-marketing-checkbox"
                      className="mt-0.5 w-4 h-4 accent-[#EAB308] flex-shrink-0"
                    />
                    <span className="text-[11px] leading-relaxed text-white/50">
                      Acepto recibir comunicaciones sobre futuros torneos y
                      novedades de PROXIMA LEAGUE (opcional).
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="form-submit-btn"
                    className="group w-full mt-2 inline-flex items-center justify-center gap-3 bg-[#EAB308] text-black px-8 py-5 text-sm tracking-[0.2em] uppercase font-bold hover:bg-[#facc15] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(234,179,8,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enviando solicitud...
                      </>
                    ) : (
                      <>
                        Solicitar inscripción
                        <span className="text-lg leading-none group-hover:translate-x-1 transition-transform">
                          →
                        </span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .inputx {
          width: 100%;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          padding: 14px 16px;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .inputx::placeholder { color: rgba(255,255,255,0.3); }
        .inputx:focus {
          border-color: #EAB308;
          box-shadow: 0 0 0 1px #EAB308;
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, icon, required, children }) => (
  <label className="block">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[#EAB308]/70">{icon}</span>
      <span className="text-[10px] tracking-[0.25em] uppercase text-white/60">
        {label}
        {required && <span className="text-[#EAB308] ml-1">*</span>}
      </span>
    </div>
    {children}
  </label>
);

const SuccessState = ({ whatsappLink, onReset }) => (
  <div data-testid="form-success" className="text-center py-6">
    <div className="inline-flex w-16 h-16 items-center justify-center border border-green-400/40 bg-green-400/5 mb-6">
      <CheckCircle2 className="w-8 h-8 text-green-400" strokeWidth={1.5} />
    </div>
    <h3 className="font-display text-3xl sm:text-4xl uppercase text-white mb-4 tracking-tight">
      Solicitud Recibida
    </h3>
    <p className="text-white/70 font-body text-base sm:text-lg max-w-md mx-auto mb-8">
      Tu solicitud ha sido recibida. Nos pondremos en contacto contigo pronto.
    </p>

    <div className="space-y-3 max-w-sm mx-auto">
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="form-success-whatsapp"
        className="group flex items-center justify-center gap-3 bg-[#EAB308] text-black px-6 py-4 text-sm tracking-[0.2em] uppercase font-bold hover:bg-[#facc15] transition-all hover:-translate-y-0.5"
      >
        Completar por WhatsApp
        <span className="text-lg leading-none group-hover:translate-x-1 transition-transform">→</span>
      </a>
      <button
        onClick={onReset}
        className="w-full border border-white/20 text-white px-6 py-4 text-xs tracking-[0.2em] uppercase font-bold hover:border-white hover:bg-white/5 transition-all"
      >
        Enviar otra solicitud
      </button>
    </div>
  </div>
);

export default Inscripciones;
